import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useToken } from '@hooks'
import { Dispatch, RootState } from '@stores'
import { ECurrency, isoToECurrency } from '@stores/types/*'
import {
  extractCurrencySymbol,
  extractMaxLimit,
  extractMinLimit,
  localeNumber,
} from '@utils'
import styled from 'styled-components'

import { DarkBgClass } from '../../../../../generated/quicktype/Tokens'
import { appClose } from '../../../../helpers/route-utils'
import { isWebview } from '../../../../helpers/webview-utils'
import {
  SESSION_GW_KEY,
  SESSION_RECEIVER_BANK_KEY,
} from '../../../../stores/payform/payForm'
import { EPaymentProvider } from '../../../../stores/types/EPaymentProvider'
import { EProduct } from '../../../../stores/types/EProduct'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { Island } from '../../../common/island'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'
import { BodyText600, CaptionText500 } from '../../../common/text/BodyText'
import { SellExchangeRate } from './SellExchangeRate'

const Container = styled.div<DarkBgClass>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  color: ${(bgClass) => bgClass.primary.default.value};
`

const ProviderBlock = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 0;
  margin-bottom: 16px;
  cursor: pointer;
`

const ProviderDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 4px;
  flex: 1 0 0;
  margin: 0 16px 16px 16px;
  width: auto;
`

const HeaderTextContainer = styled.div<DarkBgClass>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  color: ${(bgClass) => bgClass.secondary.default.value};
  margin: 0 0 16px;
  padding: 0 24px;
`

const TextContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const LabelText = styled.div`
  color: #868686;
`

const CloseContainer = styled.div`
  margin: 8px 20px 0 20px;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  product?: EProduct
  paymentGateways: TPaymentGateway[]
}

type Props = StateProps & DispatchProps

interface DispatchProps {
  setRate: (rate: number | undefined) => void
  setLimits: (minLimit: string, maxLimit: string) => void
  clearBank: () => void
  setPaymentGateway: (paymentGateway: TPaymentGateway) => void
}

export const getToSourcePath = (gw: TPaymentGateway): string => {
  // TODO: заменить source на channel или destination
  const toCurrency = isoToECurrency(gw.currencyTo)
  switch (toCurrency) {
    case ECurrency.RUB:
      return `../source/SBP`
    case ECurrency.USD:
      return `../source/BANK_CARD`
    case ECurrency.INR:
      return `../source/IMPS`
    case ECurrency.VND:
      return '../source/P2P'
    case ECurrency.THB:
      return '../source/THAI_QR'
  }
  throw new Error(
    `Can not resolve payment method for sell currency ${gw.currencyTo}`,
  )
}

const mapPaymentProviderToText = (
  provider: EPaymentProvider,
  currencyTo: ECurrency | null,
): string => {
  if (provider === EPaymentProvider.RT && currencyTo === ECurrency.RUB) {
    return 'sellProviderSelectPage.sbp'
  }
  if (provider === EPaymentProvider.PW) {
    switch (currencyTo) {
      case ECurrency.USD:
        return 'sellProviderSelectPage.visa'
      case ECurrency.UZS:
        return 'sellProviderSelectPage.uz'
      default:
        throw new Error(
          `unexpected currencyTo ${currencyTo} for EPaymentProvider ${provider}`,
        )
    }
  }
  if (provider === EPaymentProvider.SPAYZ)
    switch (currencyTo) {
      case ECurrency.INR:
        return 'sellProviderSelectPage.imps'
      case ECurrency.THB:
        return 'sellProviderSelectPage.thaiQr'
      case ECurrency.VND:
        return 'sellProviderSelectPage.p2p'
    }
  throw new Error(
    `unexpected currencyTo ${currencyTo} for EPaymentProvider ${provider}`,
  )
}

export const SellProviderSelectPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()
  const tokens = useToken()

  // Чистим выбранный банк, чтобы он не попал в другой шлюз, если пользователь его поменяет
  props.clearBank()

  const goToSourceSelectGw = (gw: TPaymentGateway) => {
    props.setPaymentGateway(gw)
    sessionStorage.setItem(SESSION_GW_KEY, gw.paymentGatewayId)

    props.setRate(gw.exchangeRate)
    props.setLimits(extractMinLimit(gw), extractMaxLimit(gw))

    redirectTo(getToSourcePath(gw))
  }

  return (
    <FlexBoxCenteredLayout>
      <Container {...tokens.color.font.light_bg}>
        <HeaderTextContainer {...tokens.color.font.light_bg}>
          <CaptionText500>
            {intl.formatMessage({ id: 'sellProviderSelectPage.title' })}
          </CaptionText500>
          <CaptionText500>
            {intl.formatMessage({ id: 'sellProviderSelectPage.rate' })}
          </CaptionText500>
        </HeaderTextContainer>
        {props.paymentGateways.map((gw) => (
          <ProviderBlock
            key={gw.paymentGatewayId}
            onClick={() => goToSourceSelectGw(gw)}
            data-testid="provider"
          >
            <Island size="M" bgColor="primary">
              <SellExchangeRate paymentGateway={gw}></SellExchangeRate>
              <ProviderDescription>
                <TextContainer data-testid="provider.limits">
                  <LabelText>
                    <CaptionText500>
                      {intl.formatMessage({
                        id: 'sellProviderSelectPage.limit',
                      })}
                    </CaptionText500>
                  </LabelText>
                  <CaptionText500>
                    {localeNumber(extractMinLimit(gw))}
                    {' – '}
                    {localeNumber(extractMaxLimit(gw))}{' '}
                    {extractCurrencySymbol(gw.currencyTo)}
                  </CaptionText500>
                </TextContainer>
                <TextContainer data-testid="provider.payment-methods">
                  <LabelText>
                    <CaptionText500>
                      {intl.formatMessage({
                        id: 'sellProviderSelectPage.payout',
                      })}
                    </CaptionText500>
                  </LabelText>
                  <CaptionText500>
                    {intl.formatMessage({
                      id: mapPaymentProviderToText(
                        gw.provider,
                        isoToECurrency(gw.currencyTo),
                      ),
                    })}
                  </CaptionText500>
                </TextContainer>
              </ProviderDescription>
            </Island>
          </ProviderBlock>
        ))}

        {!isWebview(navigator.userAgent) && (
          <CloseContainer onClick={() => appClose()}>
            <BodyText600>
              {props.product
                ? intl.formatMessage(
                    { id: 'sellProviderSelectPage.back' },
                    { product: props.product },
                  )
                : null}
            </BodyText600>
          </CloseContainer>
        )}
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  product: state.payForm?.application?.product,
  paymentGateways: state.paymentGateways?.paymentGateways ?? [],
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setRate: (rate: number | undefined) =>
      dispatch.payForm.set({
        rate,
      }),
    setLimits: (minLimit: string, maxLimit: string) => {
      dispatch.payForm.set({
        minLimit,
        maxLimit,
      })
    },
    clearBank: () => {
      sessionStorage.removeItem(SESSION_RECEIVER_BANK_KEY)
      dispatch.payForm.set({
        receiverBank: undefined,
      })
    },
    setPaymentGateway: (paymentGateway: TPaymentGateway) => {
      dispatch.payForm.set({
        paymentGateway,
      })
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SellProviderSelectPage)
